/**
 * ReadMore: collapses overflowing text to N lines with an accessible toggle.
 * Usage: call ReadMore.init() once after the DOM is ready.
 */
const ReadMore = (() => {
  let uid = 0;

  function pxPerLine(el) {
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight);
    const fontSize = parseFloat(cs.fontSize);
    // Normalize: some browsers may return "normal" for line-height
    const lh = Number.isFinite(lineHeight) ? lineHeight : 1.5 * fontSize;
    return lh;
  }

  function needsClamp(el, maxLines) {
    const original = el.style.maxHeight;
    el.style.maxHeight = 'none';
    const fullHeight = el.scrollHeight;
    const perLine = pxPerLine(el);
    el.style.maxHeight = original;
    return fullHeight > perLine * maxLines + 1; // tiny buffer
  }

  function enhance(container, opts) {
    const maxLines = Number(container.dataset.readmore) || opts.maxLines;
    container.style.setProperty('--rm-lines', maxLines);

    // Wrap inner content so we can animate height cleanly
    const content = document.createElement('div');
    content.className = 'rm__content';
    content.innerHTML = container.innerHTML;
    container.innerHTML = '';
    container.appendChild(content);

    const fade = document.createElement('div');
    fade.className = 'rm__fade';
    container.appendChild(fade);

    // If clamping isn't required, bail early (no button, no changes)
    if (!needsClamp(content, maxLines)) {
      fade.remove();
      container.classList.add('rm--expanded'); // always expanded on load
      return;
    }

    // Compute and set the CSS custom line-height for nicer clamping
    const perLine = pxPerLine(content);
    const fontSize = parseFloat(getComputedStyle(content).fontSize) || 16;
    container.style.setProperty('--rm-line-height', (perLine / fontSize).toString());

    // Add the button
    const id = `rm-${++uid}`;
    content.id = id;

    const btn = document.createElement('button');
    btn.className = 'rm__btn';
    btn.type = 'button';
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', id);
    btn.textContent = opts.moreText;

    btn.addEventListener('click', () => {
      const expanded = container.classList.toggle('rm--expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.textContent = expanded ? opts.lessText : opts.moreText;
    });

    container.appendChild(btn);

    // Keep things correct on layout changes (font swaps, viewport changes, etc.)
    const ro = new ResizeObserver(() => {
      // If content now fits, remove controls; if it doesn't, ensure controls exist.
      if (!needsClamp(content, maxLines)) {
        container.classList.add('rm--expanded');
        fade.style.display = 'none';
        btn.style.display = 'none';
      } else {
        fade.style.display = '';
        btn.style.display = '';
      }
    });
    ro.observe(content);
  }

  return {
    init(options = {}) {
      const opts = {
        selector: '.rm--true',
        maxLines: 4,
        moreText: 'Read more',
        lessText: 'Show less',
        ...options
      };
      document.querySelectorAll(opts.selector).forEach(el => {
        if (!el.classList.contains('rm--ready')) {
          el.classList.add('rm--ready');
          el.classList.add('rm--true'); // ensure base class is present
          enhance(el, opts);
        }
      });
    }
  };
})();

// Kick it off after DOM is ready
if (document.readyState !== 'loading') {
  ReadMore.init({ maxLines: 4 });
} else {
  document.addEventListener('DOMContentLoaded', () => ReadMore.init({ maxLines: 4 }));
}